import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useAccess } from "../api/access";
import { api } from "../api/client";
import { deriveSpecies, isManuallyTagged, type Shot } from "../api/types";
import { LikeCommentBar } from "./LikeCommentBar";
import { fmtDate, fmtTime, manualTagTooltip, SPECIES_LABEL } from "./format";

interface Props {
  shot: Shot;
  onClose: () => void;
  /** Called after the image is successfully hidden via POST /hide. */
  onHidden?: (imageId: string) => void;
  /** Called with the refreshed shot after a manual tag change. */
  onChanged?: (shot: Shot) => void;
  /** Bound to ArrowLeft. Omit to disable that nav direction. */
  onPrev?: () => void;
  /** Bound to ArrowRight. Omit to disable that nav direction. */
  onNext?: () => void;
  /** Bound to PageUp. Omit when day-stepping doesn't apply. */
  onPrevDay?: () => void;
  /** Bound to PageDown. Omit when day-stepping doesn't apply. */
  onNextDay?: () => void;
}

function fmtCoords(lat: number, lon: number): string {
  return `${lat.toFixed(5)}, ${lon.toFixed(5)}`;
}

function mapsUrl(lat: number, lon: number): string {
  // Google Maps' search URL renders a pin at the given coords and works
  // without any API key. `query=` is the documented param.
  return `https://www.google.com/maps/search/?api=1&query=${lat},${lon}`;
}

type EnhancerKey = "brightness" | "contrast" | "lift" | "saturate";

interface EnhancerConfig {
  key: EnhancerKey;
  label: string;
  min: number;
  max: number;
  /** Value applied when the chip is toggled on by a plain click. */
  initial: number;
  /** Where on the 0–1 strength axis the "off" value sits. */
  offValue: number;
}

// Brightness/contrast/saturate are CSS filter multipliers (1 = identity).
// "lift" is a gamma exponent applied via the inline SVG filter below — lower
// exponent lifts shadows harder, so its strength axis is inverted (min = max
// effect). Ranges are picked to handle dusk frames where the subject sits in
// shadow under a bright sky without blowing the highlights to pure white.
const ENHANCERS: readonly EnhancerConfig[] = [
  { key: "brightness", label: "Brightness", min: 1, max: 3, initial: 1.6, offValue: 1 },
  { key: "contrast", label: "Contrast", min: 1, max: 2.5, initial: 1.3, offValue: 1 },
  { key: "lift", label: "Lift shadows", min: 0.3, max: 1, initial: 0.55, offValue: 1 },
  { key: "saturate", label: "Saturation", min: 1, max: 2.5, initial: 1.4, offValue: 1 },
];

type EnhancerState = Record<EnhancerKey, number | null>;

const INITIAL_STATE: EnhancerState = {
  brightness: null,
  contrast: null,
  lift: null,
  saturate: null,
};

function clamp(v: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, v));
}

/** Strength along a 0..1 axis, accounting for inverted (lift) ranges. */
function strength(cfg: EnhancerConfig, v: number): number {
  if (cfg.min > cfg.max) return 0; // defensive
  const inverted = cfg.offValue >= cfg.max; // lift: off = 1, max effect = min
  const t = (v - cfg.min) / (cfg.max - cfg.min);
  return clamp(inverted ? 1 - t : t, 0, 1);
}

function buildFilter(state: EnhancerState, gammaId: string): string {
  const parts: string[] = [];
  if (state.brightness != null) parts.push(`brightness(${state.brightness.toFixed(3)})`);
  if (state.contrast != null) parts.push(`contrast(${state.contrast.toFixed(3)})`);
  if (state.saturate != null) parts.push(`saturate(${state.saturate.toFixed(3)})`);
  if (state.lift != null) parts.push(`url(#${gammaId})`);
  return parts.length ? parts.join(" ") : "none";
}

interface ChipProps {
  config: EnhancerConfig;
  value: number | null;
  onChange: (next: number | null) => void;
}

function EnhancerChip({ config, value, onChange }: ChipProps) {
  const drag = useRef<{
    startX: number;
    startValue: number;
    moved: boolean;
  } | null>(null);

  const onPointerDown = (e: React.PointerEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    e.currentTarget.setPointerCapture(e.pointerId);
    drag.current = {
      startX: e.clientX,
      startValue: value ?? config.initial,
      moved: false,
    };
  };

  const onPointerMove = (e: React.PointerEvent<HTMLButtonElement>) => {
    const s = drag.current;
    if (!s) return;
    const dx = e.clientX - s.startX;
    if (Math.abs(dx) > 4) s.moved = true;
    if (!s.moved) return;
    // 220px of horizontal travel maps to the chip's full range — feels close
    // to a real slider while still letting tiny taps register as clicks.
    const range = config.max - config.min;
    const next = clamp(s.startValue + (dx / 220) * range, config.min, config.max);
    onChange(next);
  };

  const onPointerUp = (e: React.PointerEvent<HTMLButtonElement>) => {
    const s = drag.current;
    drag.current = null;
    if (!s) return;
    if (e.currentTarget.hasPointerCapture(e.pointerId)) {
      e.currentTarget.releasePointerCapture(e.pointerId);
    }
    if (!s.moved) onChange(value == null ? config.initial : null);
  };

  const active = value != null;
  const pct = active ? Math.round(strength(config, value!) * 100) : 0;

  return (
    <button
      type="button"
      className={`lightbox-fx${active ? " on" : ""}`}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={() => {
        drag.current = null;
      }}
      onClick={(e) => e.stopPropagation()}
      aria-pressed={active}
      title={`${config.label} — tap to toggle, drag to adjust`}
      style={{ ["--fx-fill" as string]: `${pct}%` }}
    >
      <span className="lightbox-fx-label">{config.label}</span>
      {active && <span className="lightbox-fx-val">{pct}%</span>}
    </button>
  );
}

export function Lightbox({
  shot,
  onClose,
  onHidden,
  onChanged,
  onPrev,
  onNext,
  onPrevDay,
  onNextDay,
}: Props) {
  // Hiding pulls a frame out of everyone's gallery, so it is team-members-only.
  // scripts/unhide.sh is the way back.
  const { canHide } = useAccess();
  const [enhancers, setEnhancers] = useState<EnhancerState>(INITIAL_STATE);
  const [hiding, setHiding] = useState(false);
  // Tracks the first touch point of a swipe so touchend can decide whether it
  // was a horizontal flick worth treating as prev/next navigation.
  const swipeStart = useRef<{ x: number; y: number } | null>(null);

  // Each frame deserves a fresh evaluation — sliders carried over from the
  // previous shot would just confuse the user when they navigate.
  useEffect(() => {
    setEnhancers(INITIAL_STATE);
  }, [shot.imageId]);

  const onHide = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (hiding) return;
    setHiding(true);
    try {
      await api.hideImage(shot.imageId);
      onHidden?.(shot.imageId);
      onClose();
    } catch (err) {
      setHiding(false);
      // Surface the failure but don't crash the lightbox — user can retry or
      // close out.
      console.error("hide failed", err);
    }
  };
  // Stable per-mount id so two lightboxes (if ever rendered) don't collide on
  // the SVG filter URL.
  const gammaId = useMemo(
    () => `lb-gamma-${Math.random().toString(36).slice(2, 9)}`,
    [],
  );

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      // Don't hijack arrow keys when the comment input (or any other field
      // rendered inside the lightbox) has focus.
      const t = e.target as HTMLElement | null;
      if (
        t &&
        (t.tagName === "INPUT" ||
          t.tagName === "TEXTAREA" ||
          t.isContentEditable)
      ) {
        return;
      }
      if (e.key === "Escape") {
        onClose();
        return;
      }
      const handler =
        e.key === "ArrowLeft"
          ? onPrev
          : e.key === "ArrowRight"
            ? onNext
            : e.key === "PageUp"
              ? onPrevDay
              : e.key === "PageDown"
                ? onNextDay
                : undefined;
      if (handler) {
        e.preventDefault();
        handler();
      }
    };
    window.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose, onPrev, onNext, onPrevDay, onNextDay]);

  const captured = new Date(shot.capturedAt);
  const species = deriveSpecies(shot.analysis);
  const edited = isManuallyTagged(shot.analysis);
  const filter = buildFilter(enhancers, gammaId);
  const liftExp = enhancers.lift ?? 1;
  const anyActive = ENHANCERS.some((c) => enhancers[c.key] != null);

  const updateEnhancer = (key: EnhancerKey, next: number | null) => {
    setEnhancers((prev) => ({ ...prev, [key]: next }));
  };

  // Touch swipe navigation, mirroring the ArrowLeft/ArrowRight keys: swiping
  // right (dragging the image rightward) reveals the previous shot, swiping
  // left reveals the next — the standard photo-viewer convention.
  const SWIPE_MIN_PX = 50;
  const onTouchStart = (e: React.TouchEvent) => {
    if (e.touches.length !== 1) {
      swipeStart.current = null;
      return;
    }
    const t = e.touches[0];
    swipeStart.current = { x: t.clientX, y: t.clientY };
  };
  const onTouchEnd = (e: React.TouchEvent) => {
    const start = swipeStart.current;
    swipeStart.current = null;
    if (!start) return;
    const t = e.changedTouches[0];
    const dx = t.clientX - start.x;
    const dy = t.clientY - start.y;
    // Require a clearly horizontal gesture so vertical scrolls and pinches
    // don't trigger navigation.
    if (Math.abs(dx) < SWIPE_MIN_PX || Math.abs(dx) <= Math.abs(dy)) return;
    const handler = dx > 0 ? onPrev : onNext;
    handler?.();
  };

  return createPortal(
    <div
      className="lightbox"
      role="dialog"
      aria-modal="true"
      aria-label={`${shot.filename} preview`}
      onClick={(e) => {
        e.stopPropagation();
        onClose();
      }}
    >
      <div className="lightbox-top" onClick={(e) => e.stopPropagation()}>
        <div className="lightbox-cam">
          <span className="dot" />
          {shot.cameraId}
        </div>
        <div
          className="lightbox-fx-bar"
          onPointerDown={(e) => e.stopPropagation()}
        >
          {ENHANCERS.map((cfg) => (
            <EnhancerChip
              key={cfg.key}
              config={cfg}
              value={enhancers[cfg.key]}
              onChange={(next) => updateEnhancer(cfg.key, next)}
            />
          ))}
          {anyActive && (
            <button
              type="button"
              className="lightbox-fx-reset"
              onClick={(e) => {
                e.stopPropagation();
                setEnhancers(INITIAL_STATE);
              }}
              title="Clear all adjustments"
            >
              Reset
            </button>
          )}
        </div>
        {canHide && (
          <button
            type="button"
            className="lightbox-hide"
            onClick={onHide}
            disabled={hiding}
            title="Hide this image from the gallery"
          >
            {hiding ? "Hiding…" : "Hide"}
          </button>
        )}
        <button
          type="button"
          className="lightbox-close"
          aria-label="Close"
          onClick={onClose}
        >
          <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
            <path
              d="M6 6 L18 18 M18 6 L6 18"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
        </button>
      </div>

      <svg width="0" height="0" style={{ position: "absolute" }} aria-hidden="true">
        <filter id={gammaId} colorInterpolationFilters="sRGB">
          <feComponentTransfer>
            <feFuncR type="gamma" amplitude={1} exponent={liftExp} offset={0} />
            <feFuncG type="gamma" amplitude={1} exponent={liftExp} offset={0} />
            <feFuncB type="gamma" amplitude={1} exponent={liftExp} offset={0} />
          </feComponentTransfer>
        </filter>
      </svg>

      <div
        className="lightbox-stage"
        onClick={(e) => e.stopPropagation()}
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
      >
        <img
          className="lightbox-img"
          src={api.imageContentUrl(shot.imageId)}
          alt={`${shot.cameraId} ${fmtDate(captured)} ${fmtTime(captured)}`}
          style={{ filter }}
        />
      </div>

      <div className="lightbox-bottom" onClick={(e) => e.stopPropagation()}>
        <span className="lightbox-filename">{shot.filename}</span>
        <span className="sep">·</span>
        <span
          className={`shot-tag lightbox-tag ${species ?? "untagged"}${edited ? " edited" : ""}`}
          title={edited ? manualTagTooltip(shot.analysis) : "AI species tag"}
        >
          {species ? SPECIES_LABEL[species] : "Untagged"}
          {edited && (
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path
                d="M4 20h4L18.5 9.5a2 2 0 0 0 0-2.8l-1.2-1.2a2 2 0 0 0-2.8 0L4 16v4z"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinejoin="round"
              />
            </svg>
          )}
        </span>
        <span className="sep">·</span>
        <span>{fmtDate(captured)}</span>
        <span>{fmtTime(captured)}</span>
        <span className="sep">·</span>
        {shot.lat != null && shot.lon != null ? (
          <a
            className="lightbox-coords"
            href={mapsUrl(shot.lat, shot.lon)}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            title="Open in Google Maps"
          >
            {fmtCoords(shot.lat, shot.lon)}
          </a>
        ) : (
          <span>Unknown location</span>
        )}
      </div>

      <div className="lightbox-social" onClick={(e) => e.stopPropagation()}>
        <LikeCommentBar
          imageId={shot.imageId}
          variant="full"
          analysis={shot.analysis}
          onChanged={onChanged}
        />
      </div>
    </div>,
    document.body,
  );
}
